/**
 * ConfigSection 規劃設定區段元件
 *
 * 提供座位規劃的各項設定：
 * - 窗戶方向
 * - 辦公桌設定（包含數量設定方式）
 * - 會議室設定
 * - 家具擺放順序
 */

import { Section } from '@/components/layout/Section';
import { SelectInput, NumberInput } from '@/components/shared/FormInput';
import { plannerStore, updateSettings } from '@/store/planner-store';
import type { PlannerSettings, WindowDirection, DeskCountMode, FurnitureOrderItem } from '@/types';

// ========================================================================
// 型別定義
// ========================================================================

export interface ConfigSectionOptions {
  /** 開始規劃回呼 */
  onStartPlanning?: (settings: PlannerSettings) => void;
  /** 快速規劃回呼（只產生一個方案） */
  onQuickPlan?: (settings: PlannerSettings) => void;
}

// ========================================================================
// 常數
// ========================================================================

/** 預設家具擺放順序 */
const DEFAULT_FURNITURE_ORDER: FurnitureOrderItem[] = [
  { id: 'supervisor', label: '主管室', order: 0 },
  { id: 'large_meeting', label: '大會議室', order: 1 },
  { id: 'medium_meeting', label: '中會議室', order: 2 },
  { id: 'small_meeting', label: '小會議室', order: 3 },
  { id: 'desk', label: '辦公桌椅', order: 4 },
];

// ========================================================================
// ConfigSection 類別
// ========================================================================

export class ConfigSection {
  private section: Section;
  private options: ConfigSectionOptions;
  private unsubscribe: (() => void) | null = null;

  // 表單元件
  private windowDirectionSelect: SelectInput;
  private deskWidthInput: NumberInput;
  private deskHeightInput: NumberInput;
  private chairDepthInput: NumberInput;
  private peopleDensityInput: NumberInput;
  private customDeskCountInput: NumberInput;

  // 會議室設定
  private supervisorCountInput: NumberInput;
  private supervisorWidthInput: NumberInput;
  private supervisorHeightInput: NumberInput;

  private largeMeetingCountInput: NumberInput;
  private largeMeetingWidthInput: NumberInput;
  private largeMeetingHeightInput: NumberInput;

  private mediumMeetingCountInput: NumberInput;
  private mediumMeetingWidthInput: NumberInput;
  private mediumMeetingHeightInput: NumberInput;

  private smallMeetingCountInput: NumberInput;
  private smallMeetingWidthInput: NumberInput;
  private smallMeetingHeightInput: NumberInput;

  // 辦公桌數量設定狀態
  private deskCountMode: DeskCountMode = 'max';

  // 家具擺放順序狀態
  private furnitureOrder: FurnitureOrderItem[] = [...DEFAULT_FURNITURE_ORDER];

  constructor(options: ConfigSectionOptions = {}) {
    this.options = options;

    // 建立區段
    this.section = new Section({
      title: '步驟 2：規劃參數設定',
      description: '設定辦公空間的各項參數',
      stepNumber: 2,
      visible: false,
    });

    // 初始化表單元件
    this.windowDirectionSelect = new SelectInput({
      options: [
        { value: 'north', label: '北' },
        { value: 'south', label: '南' },
        { value: 'east', label: '東' },
        { value: 'west', label: '西' },
      ],
      value: 'north',
      onChange: (value) => this.updateSetting('windowDirection', value as WindowDirection),
    });

    // 辦公桌設定（單位：公尺）
    this.deskWidthInput = new NumberInput({
      value: '1.2',
      min: 0.6,
      max: 2.0,
      step: 0.1,
      onChange: (value) => this.updateSetting('deskWidth', parseFloat(value)),
    });

    this.deskHeightInput = new NumberInput({
      value: '0.7',
      min: 0.4,
      max: 1.0,
      step: 0.05,
      onChange: (value) => this.updateSetting('deskHeight', parseFloat(value)),
    });

    this.chairDepthInput = new NumberInput({
      value: '0.5',
      min: 0.3,
      max: 0.8,
      step: 0.05,
      onChange: (value) => this.updateSetting('chairDepth', parseFloat(value)),
    });

    this.peopleDensityInput = new NumberInput({
      value: '2.5',
      min: 1,
      max: 20,
      step: 0.1,
      onChange: (value) => this.updateSetting('peopleDensity', parseFloat(value)),
    });

    this.customDeskCountInput = new NumberInput({
      value: '10',
      min: 1,
      max: 500,
      step: 1,
    });

    // 主管室設定（單位：公尺）
    this.supervisorCountInput = new NumberInput({ value: '1', min: 0, max: 10 });
    this.supervisorWidthInput = new NumberInput({ value: '3.0', min: 2.0, max: 5.0, step: 0.1 });
    this.supervisorHeightInput = new NumberInput({ value: '3.5', min: 2.0, max: 5.0, step: 0.1 });

    // 大會議室設定（單位：公尺）
    this.largeMeetingCountInput = new NumberInput({ value: '1', min: 0, max: 5 });
    this.largeMeetingWidthInput = new NumberInput({ value: '7.0', min: 4.0, max: 10.0, step: 0.5 });
    this.largeMeetingHeightInput = new NumberInput({ value: '4.2', min: 3.0, max: 8.0, step: 0.5 });

    // 中會議室設定（單位：公尺）
    this.mediumMeetingCountInput = new NumberInput({ value: '1', min: 0, max: 10 });
    this.mediumMeetingWidthInput = new NumberInput({ value: '5.2', min: 3.0, max: 6.0, step: 0.5 });
    this.mediumMeetingHeightInput = new NumberInput({ value: '4.2', min: 2.0, max: 5.0, step: 0.5 });

    // 小會議室設定（單位：公尺）
    this.smallMeetingCountInput = new NumberInput({ value: '1', min: 0, max: 20 });
    this.smallMeetingWidthInput = new NumberInput({ value: '4.6', min: 2.0, max: 5.0, step: 0.25 });
    this.smallMeetingHeightInput = new NumberInput({ value: '3.4', min: 2.0, max: 5.0, step: 0.25 });

    this.assembleUI();
    this.subscribeToStore();
  }

  // ========================================================================
  // 公開方法
  // ========================================================================

  getElement(): HTMLElement {
    return this.section.getElement();
  }

  show(): void {
    this.section.show();
  }

  hide(): void {
    this.section.hide();
  }

  mount(container: HTMLElement): void {
    this.section.mount(container);
  }

  destroy(): void {
    this.unsubscribe?.();
    this.section.destroy();
  }

  // ========================================================================
  // 私有方法 - UI 建構
  // ========================================================================

  private assembleUI(): void {
    const content = this.section.getContent();

    content.innerHTML = `
      <div class="space-y-6">
        <!-- 窗戶方向 -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">窗戶方向</label>
            <div id="windowDirectionContainer"></div>
          </div>
        </div>

        <!-- 辦公桌設定 -->
        <div class="border-t pt-4">
          <h3 class="text-sm font-semibold text-gray-900 mb-3">辦公桌設定</h3>

          <!-- 辦公桌數量設定方式 -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">辦公桌數量設定方式</label>
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <input type="radio" id="deskCountAuto" name="deskCountMode" value="auto"
                  class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300">
                <label for="deskCountAuto" class="text-sm text-gray-700">依照每人所需坪數，自動計算桌數。人均坪數：</label>
                <div id="peopleDensityContainer" class="w-24"></div>
                <span class="text-xs text-gray-500">坪/人</span>
              </div>
              <div class="flex items-center gap-2">
                <input type="radio" id="deskCountMax" name="deskCountMode" value="max" checked
                  class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300">
                <label for="deskCountMax" class="text-sm text-gray-700">依可用空間計算最大桌數</label>
              </div>
              <div class="flex items-center gap-2">
                <input type="radio" id="deskCountCustom" name="deskCountMode" value="custom"
                  class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300">
                <label for="deskCountCustom" class="text-sm text-gray-700">指定桌數</label>
                <div id="customDeskCountContainer" class="w-24"></div>
              </div>
            </div>
          </div>

          <!-- 辦公桌尺寸 -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">桌面寬度 (m)</label>
              <div id="deskWidthContainer"></div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">桌面深度 (m)</label>
              <div id="deskHeightContainer"></div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">椅子深度 (m)</label>
              <div id="chairDepthContainer"></div>
            </div>
          </div>
        </div>

        <!-- 主管室設定 -->
        <div class="border-t pt-4">
          <h3 class="text-sm font-semibold text-gray-900 mb-3">主管室設定</h3>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">數量</label>
              <div id="supervisorCountContainer"></div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">寬度 (m)</label>
              <div id="supervisorWidthContainer"></div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">深度 (m)</label>
              <div id="supervisorHeightContainer"></div>
            </div>
          </div>
        </div>

        <!-- 會議室設定 -->
        <div class="border-t pt-4">
          <h3 class="text-sm font-semibold text-gray-900 mb-3">會議室設定</h3>

          <!-- 大會議室 -->
          <div class="mb-4">
            <p class="text-sm text-gray-600 mb-2">大會議室（10人以上）</p>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-xs text-gray-500 mb-1">數量</label>
                <div id="largeMeetingCountContainer"></div>
              </div>
              <div>
                <label class="block text-xs text-gray-500 mb-1">寬度 (m)</label>
                <div id="largeMeetingWidthContainer"></div>
              </div>
              <div>
                <label class="block text-xs text-gray-500 mb-1">深度 (m)</label>
                <div id="largeMeetingHeightContainer"></div>
              </div>
            </div>
          </div>

          <!-- 中會議室 -->
          <div class="mb-4">
            <p class="text-sm text-gray-600 mb-2">中會議室（6-10人）</p>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-xs text-gray-500 mb-1">數量</label>
                <div id="mediumMeetingCountContainer"></div>
              </div>
              <div>
                <label class="block text-xs text-gray-500 mb-1">寬度 (m)</label>
                <div id="mediumMeetingWidthContainer"></div>
              </div>
              <div>
                <label class="block text-xs text-gray-500 mb-1">深度 (m)</label>
                <div id="mediumMeetingHeightContainer"></div>
              </div>
            </div>
          </div>

          <!-- 小會議室 -->
          <div>
            <p class="text-sm text-gray-600 mb-2">小會議室（4-6人）</p>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-xs text-gray-500 mb-1">數量</label>
                <div id="smallMeetingCountContainer"></div>
              </div>
              <div>
                <label class="block text-xs text-gray-500 mb-1">寬度 (m)</label>
                <div id="smallMeetingWidthContainer"></div>
              </div>
              <div>
                <label class="block text-xs text-gray-500 mb-1">深度 (m)</label>
                <div id="smallMeetingHeightContainer"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- 擺放順序設定 -->
        <div class="border-t pt-4">
          <h3 class="text-sm font-semibold text-gray-900 mb-2">擺放順序設定</h3>
          <p class="text-xs text-gray-600 mb-3">調整家具的放置先後順序，優先放置的家具會佔據較佳位置</p>
          <div class="bg-gray-50 p-4 rounded-lg">
            <ul id="furnitureOrderList" class="space-y-2 mb-3">
              <!-- JavaScript 動態生成 -->
            </ul>
            <button type="button" id="resetOrderBtn" class="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors">
              🔄 重置為預設順序
            </button>
          </div>
        </div>

        <!-- 操作按鈕 -->
        <div class="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button type="button" id="quickPlanBtn" class="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            快速規劃
          </button>
          <button type="button" id="startPlanBtn" class="px-6 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
            開始規劃
          </button>
        </div>
      </div>
    `;

    // 掛載所有輸入元件
    this.mountInputs(content);
    this.bindEvents(content);
    this.renderFurnitureOrderList();
    this.updateDeskCountInputStates();
  }

  private mountInputs(content: HTMLElement): void {
    const mounts: [string, { mount: (el: HTMLElement) => void }][] = [
      ['#windowDirectionContainer', this.windowDirectionSelect],
      ['#peopleDensityContainer', this.peopleDensityInput],
      ['#customDeskCountContainer', this.customDeskCountInput],
      ['#deskWidthContainer', this.deskWidthInput],
      ['#deskHeightContainer', this.deskHeightInput],
      ['#chairDepthContainer', this.chairDepthInput],
      ['#supervisorCountContainer', this.supervisorCountInput],
      ['#supervisorWidthContainer', this.supervisorWidthInput],
      ['#supervisorHeightContainer', this.supervisorHeightInput],
      ['#largeMeetingCountContainer', this.largeMeetingCountInput],
      ['#largeMeetingWidthContainer', this.largeMeetingWidthInput],
      ['#largeMeetingHeightContainer', this.largeMeetingHeightInput],
      ['#mediumMeetingCountContainer', this.mediumMeetingCountInput],
      ['#mediumMeetingWidthContainer', this.mediumMeetingWidthInput],
      ['#mediumMeetingHeightContainer', this.mediumMeetingHeightInput],
      ['#smallMeetingCountContainer', this.smallMeetingCountInput],
      ['#smallMeetingWidthContainer', this.smallMeetingWidthInput],
      ['#smallMeetingHeightContainer', this.smallMeetingHeightInput],
    ];

    for (const [selector, component] of mounts) {
      const container = content.querySelector(selector);
      if (container) {
        component.mount(container as HTMLElement);
      }
    }
  }

  private bindEvents(content: HTMLElement): void {
    const quickPlanBtn = content.querySelector('#quickPlanBtn');
    const startPlanBtn = content.querySelector('#startPlanBtn');
    const resetOrderBtn = content.querySelector('#resetOrderBtn');

    quickPlanBtn?.addEventListener('click', () => this.handleQuickPlan());
    startPlanBtn?.addEventListener('click', () => this.handleStartPlan());
    resetOrderBtn?.addEventListener('click', () => this.resetFurnitureOrder());

    // 辦公桌數量設定模式變更事件
    const deskCountRadios = content.querySelectorAll('input[name="deskCountMode"]');
    deskCountRadios.forEach((radio) => {
      radio.addEventListener('change', (e) => {
        this.deskCountMode = (e.target as HTMLInputElement).value as DeskCountMode;
        this.updateDeskCountInputStates();
      });
    });
  }

  // ========================================================================
  // 私有方法 - 辦公桌數量設定
  // ========================================================================

  private updateDeskCountInputStates(): void {
    const content = this.section.getContent();

    // 人均坪數輸入框
    const peopleDensityEl = content.querySelector('#peopleDensityContainer input') as HTMLInputElement;
    if (peopleDensityEl) {
      peopleDensityEl.disabled = this.deskCountMode !== 'auto';
      peopleDensityEl.classList.toggle('bg-gray-100', this.deskCountMode !== 'auto');
    }

    // 自訂桌數輸入框
    const customDeskCountEl = content.querySelector('#customDeskCountContainer input') as HTMLInputElement;
    if (customDeskCountEl) {
      customDeskCountEl.disabled = this.deskCountMode !== 'custom';
      customDeskCountEl.classList.toggle('bg-gray-100', this.deskCountMode !== 'custom');
    }
  }

  // ========================================================================
  // 私有方法 - 擺放順序管理
  // ========================================================================

  private renderFurnitureOrderList(): void {
    const listEl = this.section.getContent().querySelector('#furnitureOrderList');
    if (!listEl) return;

    listEl.innerHTML = '';

    this.furnitureOrder.forEach((item, index) => {
      const li = document.createElement('li');
      li.className = 'flex items-center justify-between bg-white p-3 rounded border border-gray-300';

      // 左側：順序和名稱
      const leftDiv = document.createElement('div');
      leftDiv.className = 'flex items-center gap-2';

      const orderSpan = document.createElement('span');
      orderSpan.className = 'font-bold text-gray-500 w-6';
      orderSpan.textContent = `${index + 1}.`;

      const labelSpan = document.createElement('span');
      labelSpan.className = 'text-gray-800 text-sm';
      labelSpan.textContent = item.label;

      leftDiv.appendChild(orderSpan);
      leftDiv.appendChild(labelSpan);

      // 右側：上下移動按鈕
      const buttonDiv = document.createElement('div');
      buttonDiv.className = 'flex gap-1';

      // 上移按鈕
      if (index > 0) {
        const upBtn = document.createElement('button');
        upBtn.type = 'button';
        upBtn.className = 'px-2 py-1 text-sm bg-primary-100 text-primary-700 rounded hover:bg-primary-200 transition-colors';
        upBtn.textContent = '↑';
        upBtn.onclick = () => this.moveFurnitureUp(index);
        buttonDiv.appendChild(upBtn);
      }

      // 下移按鈕
      if (index < this.furnitureOrder.length - 1) {
        const downBtn = document.createElement('button');
        downBtn.type = 'button';
        downBtn.className = 'px-2 py-1 text-sm bg-primary-100 text-primary-700 rounded hover:bg-primary-200 transition-colors';
        downBtn.textContent = '↓';
        downBtn.onclick = () => this.moveFurnitureDown(index);
        buttonDiv.appendChild(downBtn);
      }

      li.appendChild(leftDiv);
      li.appendChild(buttonDiv);
      listEl.appendChild(li);
    });
  }

  private moveFurnitureUp(index: number): void {
    if (index > 0) {
      const temp = this.furnitureOrder[index - 1]!;
      this.furnitureOrder[index - 1] = this.furnitureOrder[index]!;
      this.furnitureOrder[index] = temp;
      // 更新 order 值
      this.furnitureOrder.forEach((item, i) => {
        item.order = i;
      });
      this.renderFurnitureOrderList();
    }
  }

  private moveFurnitureDown(index: number): void {
    if (index < this.furnitureOrder.length - 1) {
      const temp = this.furnitureOrder[index + 1]!;
      this.furnitureOrder[index + 1] = this.furnitureOrder[index]!;
      this.furnitureOrder[index] = temp;
      // 更新 order 值
      this.furnitureOrder.forEach((item, i) => {
        item.order = i;
      });
      this.renderFurnitureOrderList();
    }
  }

  private resetFurnitureOrder(): void {
    this.furnitureOrder = [...DEFAULT_FURNITURE_ORDER];
    this.renderFurnitureOrderList();
  }

  // ========================================================================
  // 私有方法 - 事件處理
  // ========================================================================

  private updateSetting<K extends keyof PlannerSettings>(key: K, value: PlannerSettings[K]): void {
    updateSettings({ [key]: value });
  }

  private getSettings(): PlannerSettings {
    return {
      windowDirection: this.windowDirectionSelect.getValue() as 'north' | 'east' | 'south' | 'west' | 'auto',
      deskWidth: this.deskWidthInput.getValue(),
      deskHeight: this.deskHeightInput.getValue(),
      chairDepth: this.chairDepthInput.getValue(),
      peopleDensity: this.peopleDensityInput.getValue(),
      // 辦公桌數量設定
      deskCountMode: this.deskCountMode,
      customDeskCount: this.deskCountMode === 'custom' ? this.customDeskCountInput.getValue() : undefined,
      // 家具擺放順序
      furnitureOrder: this.furnitureOrder.map((item, index) => ({ ...item, order: index })),
      // 主管室設定
      supervisorRoomCount: this.supervisorCountInput.getValue(),
      supervisorRoomWidth: this.supervisorWidthInput.getValue(),
      supervisorRoomDepth: this.supervisorHeightInput.getValue(),
      // 會議室設定
      largeMeetingRoomCount: this.largeMeetingCountInput.getValue(),
      largeMeetingRoomWidth: this.largeMeetingWidthInput.getValue(),
      largeMeetingRoomDepth: this.largeMeetingHeightInput.getValue(),
      mediumMeetingRoomCount: this.mediumMeetingCountInput.getValue(),
      mediumMeetingRoomWidth: this.mediumMeetingWidthInput.getValue(),
      mediumMeetingRoomDepth: this.mediumMeetingHeightInput.getValue(),
      smallMeetingRoomCount: this.smallMeetingCountInput.getValue(),
      smallMeetingRoomWidth: this.smallMeetingWidthInput.getValue(),
      smallMeetingRoomDepth: this.smallMeetingHeightInput.getValue(),
      // 門淨空區域預設值
      mainDoorClearance: 150,
      normalDoorClearance: 100,
      emergencyDoorClearance: 150,
    };
  }

  private handleQuickPlan(): void {
    const settings = this.getSettings();
    this.options.onQuickPlan?.(settings);
  }

  private handleStartPlan(): void {
    const settings = this.getSettings();
    this.options.onStartPlanning?.(settings);
  }

  private subscribeToStore(): void {
    this.unsubscribe = plannerStore.subscribeSelector(
      (state) => state.settings,
      (settings) => {
        // 同步 Store 狀態到表單
        if (settings.windowDirection) {
          this.windowDirectionSelect.setValue(settings.windowDirection);
        }
        if (settings.deskWidth !== undefined) {
          this.deskWidthInput.setValue(settings.deskWidth);
        }
        if (settings.deskHeight !== undefined) {
          this.deskHeightInput.setValue(settings.deskHeight);
        }
        if (settings.chairDepth !== undefined) {
          this.chairDepthInput.setValue(settings.chairDepth);
        }
        if (settings.peopleDensity !== undefined) {
          this.peopleDensityInput.setValue(settings.peopleDensity);
        }
      }
    );
  }
}
